import React from 'react';
import ContactForm from './ContactForm';
import { motion } from 'framer-motion';
import AppointmentBooking from './AppointmentBooking';
import CustomerPortal from './CustomerPortal';
import { 
  FaArrowRight, FaPlay, FaUserMd, FaHeart, FaLeaf, FaBrain, FaUsers, FaGlobe, 
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaFacebookF, FaInstagram, FaLinkedinIn, FaTwitter 
} from 'react-icons/fa';

const about = {
  heading: 'About Us',
  text: 'HealthBridge Clinic is a state-of-the-art medical facility staffed by board-certified physicians and healthcare professionals. We offer comprehensive primary and specialty care services with a patient-centered approach. Our mission is to provide exceptional healthcare with compassion, utilizing the latest medical technologies and evidence-based practices.',
  team: [
    { 
      name: 'Dr. Rohit Sharma', 
      role: 'Chief Medical Officer', 
      credentials: "MD, Internal Medicine | MBBS | Fellowship in Cardiology - AIIMS",
      img: '/images/team1.jpg' 
    },
    { 
      name: 'Dr. Priya Patel', 
      role: 'Pediatric Specialist',
      credentials: 'MD, Pediatrics | DCH | Former Head of Pediatrics at City Hospital',
      img: '/images/team2.jpg' 
    },
    { 
      name: 'Dr. Amit Verma', 
      role: 'Orthopedic Surgeon',
      credentials: 'MS Orthopedics | DNB | Fellowship in Joint Replacement Surgery',
      img: '/images/team3.jpg' 
    },
  ],
};

const services = [
  {
    icon: <FaUserMd className="text-3xl text-purple-600" />, title: 'General Medicine',
    desc: 'Comprehensive primary care for patients of all ages, including preventive health screenings.'
  },
  {
    icon: <FaHeart className="text-3xl text-purple-500" />, title: 'Cardiology',
    desc: 'Advanced cardiac care including ECG, echocardiography, and management of heart conditions.'
  },
  {
    icon: <FaLeaf className="text-3xl text-purple-400" />, title: 'Pediatrics',
    desc: 'Specialized healthcare for infants, children, and adolescents up to 18 years.'
  },
  {
    icon: <FaBrain className="text-3xl text-purple-600" />, title: 'Orthopedics',
    desc: 'Treatment for bone and joint issues, including fractures, arthritis, and sports injuries.'
  },
  {
    icon: <FaUsers className="text-3xl text-purple-500" />, title: 'Diagnostic Lab',
    desc: 'State-of-the-art laboratory testing and radiology services for accurate diagnosis.'
  },
  {
    icon: <FaGlobe className="text-3xl text-purple-400" />, title: 'Telemedicine',
    desc: 'Virtual consultations with our specialists from the comfort of your home.'
  },
];

const portfolio = [
  { img: '/images/portfolio1.jpg', title: 'New Cardiac Care Unit', desc: 'Recently inaugurated state-of-the-art cardiac monitoring and intervention facility.' },
  { img: '/images/portfolio2.jpg', title: 'Pediatric Wing Expansion', desc: 'Added specialized facilities for advanced pediatric care and treatment.' },
  { img: '/images/portfolio3.jpg', title: 'Telemedicine Platform', desc: 'Launched secure virtual consultation platform for remote patient care.' },
];

const testimonials = [
  { name: 'Rajesh Khanna', role: 'Heart Patient', avatar: '/images/client1.jpg', content: 'The cardiac team at HealthBridge Clinic saved my life. Their quick diagnosis and treatment were exceptional.' },
  { name: 'Meera Singh', role: 'Parent', avatar: '/images/client2.jpg', content: 'The pediatric staff are amazing with children. My daughter actually looks forward to her doctor visits now.' },
  { name: 'Vikram Shah', role: 'Sports Enthusiast', avatar: '/images/client3.jpg', content: 'After my knee surgery, the orthopedic rehabilitation program helped me return to playing cricket faster than expected.' },
];

const contact = {
  address: '45 Healthcare Boulevard, Powai, Mumbai, India',
  phone: '+91-22-4567-8901',
  email: 'appointments@healthbridge.com',
  map: 'https://goo.gl/maps/xyz123',
};

const social = [
  { name: 'Facebook', icon: <FaFacebookF />, url: '#' },
  { name: 'Instagram', icon: <FaInstagram />, url: '#' },
  { name: 'LinkedIn', icon: <FaLinkedinIn />, url: '#' },
  { name: 'Twitter', icon: <FaTwitter />, url: '#' },
];

const Landing = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900">
          {/* Creative background: animated gradient blobs */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
          {/* Add a subtle grid overlay for extra depth */}
          <div className="absolute inset-0 pointer-events-none" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white max-w-xl"
            >
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-purple-500/20 rounded-full text-sm font-semibold backdrop-blur-sm">
                  Expert Medical Care For Your Family
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Your Health Is
                <span className="block text-purple-300 mt-2">Our Priority</span>
                <span className="block text-purple-200 mt-2">Every Day</span>
              </h1>
              <p className="text-lg md:text-xl text-purple-100 mb-8 max-w-xl">
                Experience comprehensive healthcare services from our team of board-certified specialists. 
                From preventive care to specialized treatments, we're here for you.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold flex items-center gap-2 shadow-lg hover:bg-purple-50 transition-all duration-300 group"
                >
                  Book Appointment
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-purple-500/20 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold flex items-center gap-2 hover:bg-purple-500/30 transition-all duration-300 group"
                >
                  <FaPlay className="text-sm" /> Virtual Tour
                </motion.button>
              </div>

              {/* Stats */}
              <div className="mt-12 grid grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-300 mb-2">15k+</div>
                  <div className="text-sm text-purple-200">Patients Treated</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-300 mb-2">30+</div>
                  <div className="text-sm text-purple-200">Medical Specialists</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-300 mb-2">98%</div>
                  <div className="text-sm text-purple-200">Patient Satisfaction</div>
                </div>
              </div>
            </motion.div>

            {/* Hero Image/Animation */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative lg:max-w-2xl mx-auto w-full"
            >
              <div className="relative z-10">
                <img
                  src="/images/hero-psychology.jpg"
                  alt="Mental Wellness"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                  style={{ maxHeight: '500px' }}
                />
                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <FaUserMd className="text-purple-600 text-xl" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-800">Expert Therapists</div>
                      <div className="text-xs text-gray-500">Available 24/7</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <FaGlobe className="text-purple-600 text-xl" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-800">Global Support</div>
                      <div className="text-xs text-gray-500">Worldwide Coverage</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{
              y: [0, 10, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: "loop",
            }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
          >
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
          </motion.div>
        </div>
      </section>

      {/* Appointment Booking Section */}
      <section id="appointment-booking" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-purple-600">Book an Appointment</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Schedule your session with our team of licensed therapists and wellness experts. Fast, secure, and confidential.
            </p>
          </motion.div>
          <div className="max-w-3xl mx-auto">
            <AppointmentBooking />
          </div>
        </div>
      </section>

      {/* Customer Portal Section */}
      <section className="py-10 bg-blue-50">
        <div className="container mx-auto px-4 flex flex-col items-center">
          <h2 className="text-2xl font-bold text-blue-700 mb-2">Customer Portal: View & Reschedule My Appointments</h2>
          <p className="mb-4 text-center text-gray-600">View and reschedule your upcoming appointments easily.</p>
          <div className="w-full max-w-xl">
            <CustomerPortal />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-purple-600 font-semibold mb-4 block">Meet Our Team</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
              {about.heading}
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              {about.text}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {about.team.map((member, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={member.img} 
                    alt={member.name} 
                    className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105 will-change-transform" 
                    style={{ willChange: 'transform' }}
                  />
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-t from-purple-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
                <div className="p-6">
                  <h4 className="font-semibold text-xl text-gray-800 mb-1">{member.name}</h4>
                  <p className="text-purple-600 font-medium mb-2">{member.role}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.credentials}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-purple-600">Our Services</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Comprehensive therapy, wellness, and coaching services for individuals and organizations.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-purple-50 rounded-xl p-8 shadow-lg hover:shadow-purple-100 transition-all duration-300 hover:scale-105"
              >
                <div className="mb-4 flex justify-center">{service.icon}</div>
                <h3 className="text-xl font-semibold mb-2 text-purple-600">{service.title}</h3>
                <p className="text-gray-700">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-purple-600">Our Work</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Explore our recent projects and success stories from around the world.
            </p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-8">
            {portfolio.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6 w-80 text-center hover:scale-105 transition-transform"
              >
                <img src={item.img} alt={item.title} className="w-full h-40 object-cover rounded mb-4" />
                <h4 className="font-semibold text-lg mb-1">{item.title}</h4>
                <p className="text-gray-700 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-purple-600">What Our Clients Say</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Real stories from people and organizations we've helped.
            </p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-8">
            {testimonials.map((t, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-purple-50 rounded-xl p-8 shadow-lg w-80 text-center"
              >
                <img src={t.avatar} alt={t.name} className="w-16 h-16 rounded-full mx-auto mb-4 object-cover" />
                <h4 className="font-semibold text-lg mb-1">{t.name}</h4>
                <p className="text-purple-600 text-sm mb-2">{t.role}</p>
                <p className="text-gray-700 italic">"{t.content}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-purple-600">Contact Us</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Reach out for appointments, collaborations, or any questions.
            </p>
          </motion.div>
          <div className="flex flex-col md:flex-row justify-center items-stretch gap-12 mx-auto max-w-4xl">
            <div className="bg-white rounded-xl shadow-lg p-8 w-full md:w-1/2 mb-8 md:mb-0">
              <h3 className="text-xl font-semibold mb-4 text-purple-600">Contact Information</h3>
              <div className="flex items-center mb-3 text-gray-700"><FaMapMarkerAlt className="mr-2 text-purple-600" /> {contact.address}</div>
              <div className="flex items-center mb-3 text-gray-700"><FaPhone className="mr-2 text-purple-600" /> {contact.phone}</div>
              <div className="flex items-center mb-3 text-gray-700"><FaEnvelope className="mr-2 text-purple-600" /> {contact.email}</div>
              <a href={contact.map} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">View on Google Maps</a>
              <div className="flex space-x-4 mt-6">
                {social.map((s, idx) => (
                  <a
                    key={idx}
                    href={s.url}
                    className="text-purple-600 hover:text-purple-500 text-2xl"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Visit us on ${s.name}`}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 w-full md:w-1/2">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Join thousands of others who have transformed their lives with our international team
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-purple-600 px-8 py-3 rounded-full font-semibold inline-flex items-center gap-2"
            >
              Get Started Today <FaArrowRight />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Landing; 